import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Lightbulb } from 'lucide-react';

import { ScopeGenerationResponse, ScopeResponseLineItem, ValidationIssue } from '../../types';
import { ViewToggle } from './ViewToggle';
import { ScopeListView } from './ScopeListView';
import { ScopeCardView } from './ScopeCardView';
import { ValidationPanel, QuestionsSection } from '../ScopeReviewComponents';

// ================================================================= //
//          Gap-finder candidate detection                             //
// ================================================================= //

function isGapCandidate(item: ScopeResponseLineItem): boolean {
  return !!(item.needs_review && item.review_reason?.startsWith('Gap-finder'));
}

// ================================================================= //
//          SuggestedAdditionsPanel                                    //
// ================================================================= //

interface SuggestedAdditionsPanelProps {
  candidates: ScopeResponseLineItem[];
}

const SuggestedAdditionsPanel: React.FC<SuggestedAdditionsPanelProps> = ({ candidates }) => {
  const [open, setOpen] = useState(false);

  if (candidates.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50">
      {/* Header — always visible, click to expand */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-sm font-semibold text-amber-900">
            Suggested additions ({candidates.length})
          </span>
          <span className="text-xs text-amber-700 font-normal">
            — gap-finder proposals, not yet in your scope
          </span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-amber-600 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-amber-600 shrink-0" />
        )}
      </button>

      {/* Collapsed body */}
      {open && (
        <div className="border-t border-amber-200 px-4 py-3 space-y-2">
          <p className="text-xs text-amber-800">
            The gap-finder identified these items as commonly needed for this damage type but not
            yet in your scope. Each has been verified against the Xactimate catalog.{' '}
            <strong>Review and add manually</strong> if appropriate — they are never auto-added.
          </p>
          <ul className="space-y-2">
            {candidates.map((item, i) => (
              <li
                key={i}
                className="rounded border border-amber-200 bg-white px-3 py-2 text-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-mono font-semibold text-amber-900">
                      {item.xactimate_code}
                    </span>
                    {item.description && (
                      <span className="ml-2 text-gray-700">{item.description}</span>
                    )}
                  </div>
                  <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    Suggestion
                  </span>
                </div>
                {item.note && (
                  <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ================================================================= //
//          ScopeOutput Container                                      //
// ================================================================= //

interface ScopeOutputProps {
  scope: ScopeGenerationResponse;
  validationIssues?: ValidationIssue[];
  showDebug?: boolean;
  stepMode?: boolean;
}

/**
 * Container component that manages the view toggle and renders
 * the selected scope display (list or cards).
 *
 * Gap-finder candidates are filtered out of the main scope and shown
 * in a distinct collapsible "Suggested additions" panel below the scope.
 */
export const ScopeOutput: React.FC<ScopeOutputProps> = ({
  scope,
  validationIssues,
  showDebug = false,
  stepMode = false,
}) => {
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');
  const [showSources, setShowSources] = useState(false);

  // Split confirmed items from gap-finder candidates
  const filteredRooms = scope.rooms.map((room) => ({
    ...room,
    items: room.items.filter((item) => !isGapCandidate(item)),
  }));
  const filteredGeneral = scope.general_conditions.filter(
    (item) => !isGapCandidate(item),
  );

  // Collect all gap-finder candidates across rooms + general conditions
  const allCandidates: ScopeResponseLineItem[] = [
    ...scope.rooms.flatMap((r) => r.items.filter(isGapCandidate)),
    ...scope.general_conditions.filter(isGapCandidate),
  ];

  // Stats based on confirmed items only
  const allConfirmed = [
    ...filteredRooms.flatMap((r) => r.items),
    ...filteredGeneral,
  ];
  const totalItems = allConfirmed.length;
  const highConfidence = allConfirmed.filter((i) => i.confidence === 'high').length;
  const needsReview = allConfirmed.filter((i) => i.confidence === 'low').length;

  const allIssues = validationIssues || scope.validation_issues || [];

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Generated Scope</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {totalItems} items &middot;{' '}
            <span className="text-green-700">{highConfidence} high confidence</span>
            {needsReview > 0 && (
              <>
                {' '}&middot;{' '}
                <span className="text-red-700">{needsReview} needs review</span>
              </>
            )}
            {allCandidates.length > 0 && (
              <>
                {' '}&middot;{' '}
                <span className="text-amber-700">
                  {allCandidates.length} suggestion{allCandidates.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSources(!showSources)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              showSources
                ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
            }`}
          >
            {showSources ? 'Hide Sources' : 'Show Sources'}
          </button>
          <ViewToggle view={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {/* Scope display — gap-finder candidates filtered out */}
      {viewMode === 'list' ? (
        <ScopeListView
          rooms={filteredRooms}
          generalConditions={filteredGeneral}
          showSources={showSources}
          showDebug={showDebug}
          stepMode={stepMode}
        />
      ) : (
        <ScopeCardView
          rooms={filteredRooms}
          generalConditions={filteredGeneral}
          showSources={showSources}
          showDebug={showDebug}
          stepMode={stepMode}
        />
      )}

      {/* Suggested additions — gap-finder candidates, collapsed by default */}
      <SuggestedAdditionsPanel candidates={allCandidates} />

      {/* Validation issues */}
      {allIssues.length > 0 && <ValidationPanel issues={allIssues} />}

      {/* Questions for the estimator */}
      {scope.questions && scope.questions.length > 0 && (
        <QuestionsSection questions={scope.questions} />
      )}
    </div>
  );
};
